import React, { useEffect, useCallback, useState, useRef } from 'react';
import { Input, Tooltip } from '@getro/rombo';
import { debounce } from 'debounce';
import { HelpCircle } from 'lucide-react';
import PropTypes from 'prop-types';
import { Box, Flex, Link, Text } from 'rebass/styled-components';

import { getSearchEntryError, SEARCH_ENTRY_ERRORS } from '../../../helpers/booleanValidator';

// The length cap is opt-in: only contacts/company filters share the backend
// contract that rejects long entries, so consumers like hires stay uncapped.
const KeywordFilter = ({ value, onChange, maxEntryLength }) => {
  const [currentValue, setCurrentValue] = useState(value[0] || '');
  const [error, setError] = useState(() => getSearchEntryError(value[0] || '', { maxLength: maxEntryLength }));
  const onChangeRef = useRef(onChange);
  const maxEntryLengthRef = useRef(maxEntryLength);
  const emittedValueRef = useRef(value[0] || '');

  useEffect(() => {
    onChangeRef.current = onChange;
    maxEntryLengthRef.current = maxEntryLength;
  }, [onChange, maxEntryLength]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnChange = useCallback(
    debounce((val) => {
      const entryError = getSearchEntryError(val, { maxLength: maxEntryLengthRef.current });
      setError(entryError);
      // Block the emit so an entry the backend would reject never leaves the client.
      if (entryError) return;

      const emitted = val.trim().length > 0 ? [val] : [];
      emittedValueRef.current = emitted[0] || '';
      // Whitespace-only means no filter, so drop it locally too — the parent
      // echo is an empty array either way and would not clear the field.
      if (!emitted.length) setCurrentValue('');
      onChangeRef.current(emitted);
    }, 500),
    [],
  );

  // Adopt value changes driven from outside (e.g. AI filter, programmatic
  // clear). Echoes of our own emit are no-ops, so typing is never clobbered.
  // Any queued emit is dropped so it can't overwrite the newer value.
  useEffect(() => {
    const nextValue = value[0] || '';
    if (nextValue === emittedValueRef.current) return;

    debouncedOnChange.clear?.();
    emittedValueRef.current = nextValue;
    setCurrentValue(nextValue);
    // The error always describes what is in the field, including values the
    // parent put there — those bypass the debounced guard entirely.
    setError(getSearchEntryError(nextValue, { maxLength: maxEntryLengthRef.current }));
  }, [value, debouncedOnChange]);

  useEffect(
    () => () => {
      debouncedOnChange.clear?.();
    },
    [debouncedOnChange],
  );

  const onCurrentChange = (event) => {
    setCurrentValue(event.target.value);
    debouncedOnChange(event.target.value);
  };

  useEffect(() => {
    const handleResetFilters = () => {
      debouncedOnChange.clear?.();
      emittedValueRef.current = '';
      setCurrentValue('');
      setError(null);
    };
    // Clear all dispatches this on document and it does not bubble, so a
    // listener on window never sees it.
    document.addEventListener('resetFilters', handleResetFilters);

    return () => {
      document.removeEventListener('resetFilters', handleResetFilters);
    };
  }, [debouncedOnChange]);

  return (
    <Flex
      sx={{
        input: {
          p: '8px',
          fontSize: '14px',
        },
        flexDirection: 'column',
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          gap: '4px',
          position: 'relative',
          ':hover': {
            '.keyword-filter-tooltip': { opacity: 1 },
          },
        }}
      >
        <Input
          data-testid="keyword-filter-input"
          placeholder="Keyword search"
          value={currentValue}
          onChange={onCurrentChange}
          sx={{
            height: '32px',
            pr: '28px!important',
          }}
        />
        <Box
          className="keyword-filter-tooltip"
          sx={{ position: 'absolute', right: '8px', top: '8px', opacity: 0, transition: 'opacity 300ms' }}
        >
          <Tooltip
            interactive
            position="right-start"
            width="320px"
            trigger={<Box as={HelpCircle} size="16px" sx={{ color: 'text.subtle', outline: 'none' }} />}
            content={
              <Flex flexDirection="column" sx={{ gap: '8px' }}>
                <Text fontSize="14px" color="neutral.0">
                  Search contacts by name, LinkedIn handle and tagline, current and past job titles, industry or skills.
                </Text>
                <Text fontSize="14px" color="neutral.0">
                  You can use the boolean operators AND, OR, NOT, quotes and parentheses.{' '}
                  <Link
                    fontSize="14px"
                    color="neutral.0"
                    href="https://help.getro.com/support/solutions/articles/65000188936-how-to-use-boolean-searches-to-find-contacts"
                    target="_blank"
                    sx={{ textDecoration: 'underline' }}
                  >
                    Learn more
                  </Link>
                  .
                </Text>
              </Flex>
            }
          />
        </Box>
        {error === SEARCH_ENTRY_ERRORS.invalidBoolean && (
          <Text fontSize="12px" color="text.error">
            Invalid boolean format.{' '}
            <Link
              fontSize="12px"
              color="text.error"
              href="https://help.getro.com/support/solutions/articles/65000188936-how-to-use-boolean-searches-to-find-contacts"
              target="_blank"
              sx={{ textDecoration: 'underline' }}
            >
              Learn more
            </Link>
            .
          </Text>
        )}
        {error === SEARCH_ENTRY_ERRORS.tooLong && (
          <Text fontSize="12px" color="text.error">
            Keyword is too long (max {maxEntryLength} characters). Try splitting it into shorter terms with AND/OR.
          </Text>
        )}
      </Flex>
    </Flex>
  );
};

KeywordFilter.propTypes = {
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string])),
  onChange: PropTypes.func,
  maxEntryLength: PropTypes.number,
};

KeywordFilter.defaultProps = {
  value: [],
  onChange: () => {},
  maxEntryLength: null,
};

export default KeywordFilter;
